import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of, throwError } from 'rxjs';
import { Organization } from '../../model/Organization';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import { catchError, map, tap } from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {User} from "../../model/User";
import {AbstractLiveDataService} from "./abstract-live-data.service";
import {NotificationsService} from "./notifications.service";

@Injectable()
export class OrganizationService extends AbstractLiveDataService<Organization> {
  constructor(private httpa: HttpClient, private messageServicea: MessageService, private startupServicea: StartupService, protected notificationsService: NotificationsService) {
    super(httpa, messageServicea, startupServicea, notificationsService);
    this.startupService.setOrganizationService(this);
    this.notificationsService.setOrganizationService(this);
    this.notificationsService.startNotification();
  }

  makeObjectInstance(args: any): Organization {
    const object = new Organization(args);
    return object;
  }

  getLoggingObjectTypeName (): string {
    return "Organization";
  }

  supportsParallel(): boolean {
    return false;
  }

  getURLEndPoint (): string {
    return "organization";
  }

  getLinkData(): any[] {
    return [];
  }

  getOrganization(id: string): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/${id}`;
    this.messageService.add(`OrganizationService: fetched organization id=${id}`);
    return this.http.get<Organization>(url, this.startupService.getHttpOptions())
      .pipe(
        map(organization => {
          const p = new Organization(organization);
          return p;
        }),
        catchError(this.handleErrorX<Organization>(`getOrganization id=${id}`))
      );
  }

  getServiceProviderOrganization (serviceProviderId: string): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/serviceProviderId/${serviceProviderId}`;
    this.messageService.add(`OrganizationService: fetched organization id=${serviceProviderId}`);
    return this.http.get<Organization>(url, this.startupService.getHttpOptions())
      .pipe(
        map(organization => {
          const p = new Organization(organization);
          return p;
        }),
        catchError(this.handleErrorX<Organization>(`getOrganization id=${serviceProviderId}`))
      );
  }

  getUmbrellaOrganization (umbrellaId: string): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/umbrellaId/${umbrellaId}`;
    this.messageService.add(`OrganizationService: fetched organization id=${umbrellaId}`);
    return this.http.get<Organization>(url, this.startupService.getHttpOptions())
      .pipe(
        map(organization => {
          const p = new Organization(organization);
          return p;
        }),
        catchError(this.handleErrorX<Organization>(`getOrganization id=${umbrellaId}`))
      );
  }

  /**
   * Lightweight lookup of **active** Organizations scoped to a
   * role. Intended for the `SelectRoleComponent` role picker which
   * needs a searchable dropdown of organizations but does NOT want
   * to trigger a full `AbstractLiveDataService` load. The server
   * must return ONLY these three fields per organization:
   *   { ID, name, serviceProvider_ID }
   * so the payload stays small even for networks with thousands of
   * organizations.
   *
   * Contract: `GET {apiBaseURL}organization/activeForRole/{role}/{roleId}`
   *   - `role` must be one of `network` | `serviceProvider`.
   *   - Response shape: `{ organizations: Array<{ID:number, name:string, serviceProvider_ID:number, admin_ID?:number, adminEmail?:string}> }`.
   *   - Only rows the server considers "active" are included.
   *   - `admin_ID` / `adminEmail` are OPTIONAL â€” added 2026-08-06 to
   *     support searching the picker by the primary user's email
   *     address (see outbox
   *     `2026-08-06-select-role-active-orgs-admin-email.md`). Older
   *     server builds simply omit the two fields; the client treats
   *     them as absent and falls back to `(ID, name)`-only display.
   *   - No JOINs beyond the single admin-user lookup, no book/order
   *     counts, no user lists â€” the shape stays a minimal
   *     three-to-five-field row per org.
   * See `docs/contracts/outbox/2026-08-03-select-role-active-orgs.md`.
   */
  getActiveOrganizationsForRole(role: string, roleId: string): Observable<Array<{ID: number, name: string, serviceProvider_ID: number, admin_ID?: number, adminEmail?: string}>> {
    const url = `${environment.apiBaseURL}organization/activeForRole/${role}/${roleId}`;
    this.messageService.add(`OrganizationService: fetching active organizations for ${role}/${roleId}`);
    // NOTE: Unlike our other helpers, this one deliberately does NOT
    // swallow errors via `catchError`. `SelectRoleComponent`
    // discriminates between "server said the list is empty" and
    // "the request failed" â€” the latter surfaces a retry affordance
    // in the picker. Silently returning `[]` on failure (as the
    // shared `handleErrorX` does) made a 401/403/5xx look identical
    // to a legitimate empty result and left the operator staring at
    // an empty combobox with no explanation.
    return this.http.get<any>(url, this.startupService.getHttpOptions())
      .pipe(
        map(response => {
          if (response && Array.isArray(response.organizations)) { return response.organizations; }
          if (Array.isArray(response)) { return response; }
          return [];
        })
      );
  }

  /** PUT: update the organization on the server */
  updateOrganization (organization: Organization): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/${organization.ID}`;
    this.messageService.add(`OrganizationService: updated organization id=${organization.ID}`);
    return this.http.put(url, organization, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated organization id=${organization.ID}`)),
      catchError(this.handleErrorX<any>('updateOrganization'))
    );
  }

  /** PUT: set a user to be an admin of an organizatoin */
  grantOrganizationAdmin (user: User): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/grantOrganizationAdmin/${user.focusOrg_ID}/${user.ID}`;
    this.messageService.add(`OrganizationService: grantOrganizationAdmin organization id=${user.focusOrg_ID}`);
    return this.http.put(url, user, this.startupService.getHttpOptions()).pipe(
      tap(x => {
        user.isAdmin = 'TRUE';
        this.log(`grantOrganizationAdmin organization id=${user.focusOrg_ID}`);
      }),
      catchError(this.failLoudly<any>('grantOrganizationAdmin'))
    );
  }
  /** PUT: set a user to be an admin of an organizatoin */
  revokeOrganizationAdmin (user: User): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization/revokeOrganizationAdmin/${user.focusOrg_ID}/${user.ID}`;
    this.messageService.add(`OrganizationService: makeOrganizationAdmin organization id=${user.focusOrg_ID}`);
    return this.http.put<Organization>(url, user, this.startupService.getHttpOptions()).pipe(
      tap(x => {
        user.isAdmin = 'FALSE';
        this.log(`revokeOrganizationAdmin organization id=${user.focusOrg_ID}`);
      }),
      catchError(this.failLoudly<Organization>(`revokeOrganizationAdmin userEmail${user.email}`))
    );
  }

  // ---------------------------------------------------------------------------
  // `resendEmailInvite` was DELETED here on 2026-09-12.
  //
  // It was a second spelling of the same feature, PUTting
  // `organization/resendEmailInvite/{id}`, with no caller. The server confirmed
  // on 2026-09-02 that both routes are registered and that the organization one
  // now delegates to the same handler, so this copy added nothing except the
  // chance of the two drifting apart again - which is how the pair got out of
  // step in the first place.
  //
  // It also sent the whole `User` row as the body, which the handler never
  // reads. `UserService.resendEmailInvite` now sends `{}`.
  //
  // The live path is `UserService.resendEmailInvite`. The server is holding its
  // `organization/` route registered until we confirm this deletion; that
  // confirmation is
  // outbox/2026-09-12-resend-invite-gated-and-six-surfaces-acknowledged.md
  //
  // Do not re-add it. If a caller ever needs a resend, call UserService.
  // ---------------------------------------------------------------------------

  /**
   * Error path for operations that change what a user can GET INTO.
   *
   * Counterpart of `UserService.failLoudly` - see the long note there. In
   * short: `handleErrorX` returns `of(result)`, which EMITS, so a failed
   * privilege change ran the caller's success callback and the screen
   * confirmed a grant or revoke that never happened. Swallowing is a
   * defensible degradation for a read; for access control it is the worst
   * available behaviour, because nobody re-checks an operation they were told
   * succeeded.
   */
  private failLoudly<T> (operation: string) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';
      console.error(`${operation} failed`, error);
      this.log(`${operation} failed: ${error && error.message ? error.message : error}`);
      return throwError(() => error);
    };
  }
  addOrganization (organization: Organization, primaryUsersFullName: string, primaryUsersEmail: string, primaryUsersPhone: string): Observable<Organization> {
    const url = `${environment.apiBaseURL}organization?primaryUsersFullName=${btoa(primaryUsersFullName)}&primaryUsersEmail=${btoa(primaryUsersEmail)}&primaryUsersPhone=${btoa(primaryUsersPhone)}`;
    return this.http.post<Organization>(url, organization, this.startupService.getHttpOptions()).pipe(
      tap(o => {
        this.log(`added organization w/ id=${organization.ID}`);
        const org = new Organization(o);
        this.objects.push(org);
        // if ( org.users && org.users.length > 0) {
        //   // TODO: Add the new User to the UserService cache
        // }
      }),
      catchError(this.handleErrorX<Organization>('addOrganization'))
    );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleErrorX<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for organization consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
